import { STOREPAGE } from "../../views/store/routes";
import { STORE_EVALUATIONS_ADMIN_PAGE, STORE_QUALITY_DASHBOARD_PAGE, STORE_COVERAGE_PAGE } from "../../views/store-evaluations/routes";
import { EVALUATION_FORMS_ADMIN_PAGE } from "../../views/evaluations/routes";
import { NR1_DASHBOARD_PAGE, NR1_LINKS_ADMIN_PAGE } from "../../views/nr1/routes";
import { QUIZ_HUB_PAGE } from "../../views/quiz/routes";
import { REPORTS_PAGE } from "../../views/reports/routes";
import { FEFOPAGE } from "../../views/fefo/routes";
import {
  DP_EMPLOYEES_PAGE,
  DP_EXPIRING_DOCUMENTS_PAGE,
  DP_FGTS_PAGE,
  DP_LEAVES_PAGE,
  DP_PAYROLL_PAGE,
  DP_PROCESSES_PAGE,
  DP_PROVISIONS_PAGE,
  DP_ROSTERS_PAGE,
  DP_SETTINGS_PAGE,
  DP_TIMESHEETS_PAGE,
  DP_VACATIONS_PAGE,
} from "../../views/dp/routes";
import {
  ESTOQUE_ATUAL_PAGE,
  ESTOQUE_BALANCO_LOJAS_PAGE,
  ESTOQUE_OCORRENCIAS_PAGE,
  ESTOQUE_PRODUTOS_PAGE,
  ESTOQUE_RELATORIOS_PAGE,
} from "../../views/estoque/routes";
import { RGB_HOME_PAGE } from "../../views/rgb/routes";
import {
  FIN_CASHFLOW_PAGE,
  FIN_DRE_PAGE,
  FIN_ENTRY_NEW_PAGE,
  FIN_PAYABLES_PAGE,
  FIN_RECEIVABLES_PAGE,
  FIN_SETTINGS_TAB_PAGE,
} from "../../views/financeiro/routes";
import { ADMIN_CONFIGURACOES_PAGE, ADMIN_CONSUMO_IA_PAGE, ADMIN_LOGS_PAGE, ADMIN_USUARIOS_PAGE } from "../../views/admin-sistema/routes";

export type SubmenuLink = {
  label: string;
  to: string;
  requiresAdmin?: boolean;
  /** Vasilhame RGB: ADMIN e supervisores (`isRgbUserFromToken`). */
  requiresRgb?: boolean;
  /** Escrita no Financeiro: ADMIN e FINANCEIRO (`canWriteFinanceiroFromToken`). */
  requiresFinWrite?: boolean;
};

export type SubmenuGroup = { title: string; links: SubmenuLink[] };

/**
 * Submenus da Topbar: os mesmos atalhos (e as mesmas regras de acesso) dos
 * hubs de cada módulo. Mudou um hub, muda aqui também:
 * operacoes.tsx, processos-ze.tsx, DpPage.tsx, FinanceiroPage.tsx, EstoquePage.tsx, AdminSistemaPage.tsx.
 * `requiresAdmin` segue também o guard da rota no App.tsx: link para tela de
 * `AdminRoute` não aparece para outro papel (cairia no login).
 *
 * Fica de fora o grupo "Freelancers e escalas" de Operações, que está
 * fora de uso por decisão do usuário (01/10/2026) e aparece desabilitado no hub.
 */
export const TOPBAR_SUBMENUS: Record<string, SubmenuGroup[]> = {
  operacoes: [
    {
      title: "Lojas e qualidade",
      links: [
        { label: "Lojas", to: STOREPAGE() },
        { label: "Avaliações de loja", to: STORE_EVALUATIONS_ADMIN_PAGE() },
        { label: "Qualidade de loja", to: STORE_QUALITY_DASHBOARD_PAGE() },
        { label: "Cobertura semanal", to: STORE_COVERAGE_PAGE() },
        { label: "Formulários de perguntas", to: EVALUATION_FORMS_ADMIN_PAGE() },
      ],
    },
    {
      title: "NR1 — riscos psicossociais",
      links: [
        { label: "Links anônimos", to: NR1_LINKS_ADMIN_PAGE() },
        { label: "Riscos por loja", to: NR1_DASHBOARD_PAGE() },
      ],
    },
    {
      title: "Quiz",
      links: [{ label: "Quiz para supervisores", to: QUIZ_HUB_PAGE() }],
    },
    {
      title: "Relatórios",
      links: [{ label: "Gerar relatórios", to: REPORTS_PAGE() }],
    },
  ],
  processos: [
    {
      title: "Processos",
      links: [{ label: "Fefo/Shelf", to: FEFOPAGE() }],
    },
  ],
  dp: [
    {
      title: "Pessoas e vínculos",
      links: [
        { label: "Colaboradores", to: DP_EMPLOYEES_PAGE() },
        { label: "Admissão e desligamento", to: DP_PROCESSES_PAGE() },
        { label: "Documentos a vencer", to: DP_EXPIRING_DOCUMENTS_PAGE() },
        { label: "Cadastros", to: DP_SETTINGS_PAGE() },
      ],
    },
    {
      title: "Tempo de trabalho",
      links: [
        { label: "Férias", to: DP_VACATIONS_PAGE() },
        { label: "Afastamentos", to: DP_LEAVES_PAGE() },
        { label: "Escala da loja", to: DP_ROSTERS_PAGE() },
        { label: "Ponto e jornada", to: DP_TIMESHEETS_PAGE() },
      ],
    },
    {
      title: "Folha e recolhimentos",
      links: [
        { label: "Folha para conferência", to: DP_PAYROLL_PAGE() },
        { label: "Recolhimentos de FGTS e INSS", to: DP_FGTS_PAGE() },
        { label: "Provisões", to: DP_PROVISIONS_PAGE() },
      ],
    },
    {
      title: "NR1 — riscos psicossociais",
      links: [
        { label: "Formulários NR1", to: EVALUATION_FORMS_ADMIN_PAGE() },
        { label: "Links anônimos", to: NR1_LINKS_ADMIN_PAGE() },
        { label: "Riscos por loja", to: NR1_DASHBOARD_PAGE() },
      ],
    },
  ],
  financeiro: [
    {
      title: "Lançamentos",
      links: [
        { label: "Contas a pagar", to: FIN_PAYABLES_PAGE() },
        { label: "Contas a receber", to: FIN_RECEIVABLES_PAGE() },
        { label: "Fluxo de caixa", to: FIN_CASHFLOW_PAGE() },
        { label: "DRE por centro de custo", to: FIN_DRE_PAGE() },
        { label: "Novo lançamento", to: FIN_ENTRY_NEW_PAGE(), requiresFinWrite: true },
      ],
    },
    {
      title: "Cadastros",
      links: [
        { label: "Contas bancárias", to: FIN_SETTINGS_TAB_PAGE("contas") },
        { label: "Plano de contas", to: FIN_SETTINGS_TAB_PAGE("categorias") },
        { label: "Fornecedores e clientes", to: FIN_SETTINGS_TAB_PAGE("contrapartes") },
        { label: "Repetições", to: FIN_SETTINGS_TAB_PAGE("repeticoes") },
        { label: "Centros de custo", to: FIN_SETTINGS_TAB_PAGE("centros") },
      ],
    },
  ],
  estoque: [
    {
      title: "Contagem e controle",
      links: [
        { label: "Balanço de massa", to: ESTOQUE_BALANCO_LOJAS_PAGE() },
        { label: "Estoque atual", to: ESTOQUE_ATUAL_PAGE(), requiresAdmin: true },
        { label: "Ocorrências de estoque", to: ESTOQUE_OCORRENCIAS_PAGE(), requiresAdmin: true },
      ],
    },
    {
      title: "Vasilhame",
      links: [{ label: "Vasilhame RGB", to: RGB_HOME_PAGE(), requiresRgb: true }],
    },
    {
      title: "Cadastros e relatórios",
      links: [
        { label: "Produtos", to: ESTOQUE_PRODUTOS_PAGE(), requiresAdmin: true },
        { label: "Relatórios", to: ESTOQUE_RELATORIOS_PAGE(), requiresAdmin: true },
      ],
    },
  ],
  administracao: [
    {
      title: "Acesso",
      links: [
        { label: "Gestão de usuários", to: ADMIN_USUARIOS_PAGE() },
        { label: "Colaboradores CLT", to: DP_EMPLOYEES_PAGE() },
      ],
    },
    {
      title: "Sistema",
      links: [
        { label: "Configurações do sistema", to: ADMIN_CONFIGURACOES_PAGE() },
        { label: "Logs de auditoria", to: ADMIN_LOGS_PAGE() },
        { label: "Consumo de IA", to: ADMIN_CONSUMO_IA_PAGE() },
      ],
    },
  ],
};

/**
 * Estoque do supervisor: os hubs (`/estoque`, `/processos`) são `AdminRoute`,
 * então a Topbar dele leva direto às telas que abre. Balanço e De/Para são do
 * supervisor de campo, como os cartões da home dele.
 */
export function supervisorEstoqueSubmenu(isFieldSupervisor: boolean): SubmenuGroup[] {
  const groups: SubmenuGroup[] = [];
  if (isFieldSupervisor) {
    groups.push({
      title: "Contagem e cadastro",
      links: [
        { label: "Balanço de massa", to: ESTOQUE_BALANCO_LOJAS_PAGE() },
        { label: "Produtos e De/Para", to: ESTOQUE_PRODUTOS_PAGE() },
      ],
    });
  }
  groups.push({ title: "Vasilhame", links: [{ label: "Vasilhame RGB", to: RGB_HOME_PAGE() }] });
  return groups;
}
